import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AVATARS, AvatarBadge } from "@/components/Avatars";
import { UserPlus, Trash2, X, ShieldCheck, GraduationCap, Home, User as UserIcon, Handshake, Ban, CircleCheck, Crown, LogIn, Building2, Building, Search } from "lucide-react";

const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

const ROLE_META = {
  admin: { label: "Admin", Icon: ShieldCheck, color: "#7A3FF2" },
  teacher: { label: "Teacher", Icon: GraduationCap, color: "#0D9488" },
  parent: { label: "Parent", Icon: Home, color: "#0284C7" },
  student: { label: "Student", Icon: UserIcon, color: "#F59E0B" },
  reseller: { label: "Reseller", Icon: Handshake, color: "#E05A47" },
  school_admin: { label: "School", Icon: Building, color: "#0891B2" },
  district_admin: { label: "District", Icon: Building2, color: "#9333EA" },
};

function GrantPlanModal({ open, title, subtitle, submitLabel, onClose, onGrant }) {
  const [plan, setPlan] = useState("teacher");
  const [seats, setSeats] = useState(30);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const grant = async () => {
    setBusy(true);
    try { await onGrant(plan, Number(seats) || 30); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="w-full max-w-md rounded-[28px] bg-white p-7 shadow-2xl border border-[#E5DECF]" data-testid="grant-plan-modal">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">{title}</h3>
            <button onClick={onClose} className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
          </div>
          <p className="mt-2 font-body text-sm text-[#6B7280]">{subtitle}</p>
          <label className="mt-4 block text-sm font-bold mb-1.5">Plan</label>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="grant-plan-select" className={inputClass}>
            <option value="parent">Parent</option>
            <option value="teacher">Teacher</option>
            <option value="school">School</option>
            <option value="reseller">Reseller</option>
          </select>
          <label className="mt-4 block text-sm font-bold mb-1.5">Seat limit</label>
          <input type="number" value={seats} onChange={(e) => setSeats(e.target.value)} className={inputClass} />
          <button onClick={grant} disabled={busy} data-testid="grant-plan-submit" className="mt-5 w-full rounded-2xl bg-[#0D9488] px-6 py-3.5 font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
            {busy ? "Granting..." : (submitLabel || "Grant plan")}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AddUserModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", role: "teacher", email: "", username: "", password: "", avatar: AVATARS[0].key });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isStudent = form.role === "student";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { name: form.name, role: form.role, password: form.password };
      if (isStudent) { payload.username = form.username; payload.avatar = form.avatar; }
      else { payload.email = form.email; }
      await api.createUser(payload);
      toast.success(`${form.name} created`);
      setForm({ name: "", role: "teacher", email: "", username: "", password: "", avatar: AVATARS[0].key });
      onCreated();
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-md rounded-[28px] bg-white p-7 shadow-2xl border border-[#E5DECF]" data-testid="add-user-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-extrabold text-[#1F2937]">Add User</h3>
              <button onClick={onClose} className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              {error && <div data-testid="add-user-error" className="rounded-2xl bg-[#E05A47]/10 px-4 py-2.5 text-sm font-semibold text-[#B4402F]">{error}</div>}
              <div>
                <label className="block text-sm font-bold mb-1.5">Role</label>
                <select data-testid="user-role-field" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputClass}>
                  <option value="admin">Admin</option>
                  <option value="teacher">Teacher</option>
                  <option value="parent">Parent</option>
                  <option value="reseller">Reseller</option>
                  <option value="school_admin">School Admin</option>
                  <option value="district_admin">District Admin</option>
                  <option value="student">Student</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5">Name</label>
                <input data-testid="user-name-field" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputClass} required />
              </div>
              {isStudent ? (
                <div>
                  <label className="block text-sm font-bold mb-1.5">Username</label>
                  <input data-testid="user-username-field" value={form.username} onChange={(e) => set("username", e.target.value)} className={inputClass} required />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-bold mb-1.5">Email</label>
                  <input data-testid="user-email-field" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} required />
                </div>
              )}
              <div>
                <label className="block text-sm font-bold mb-1.5">Password</label>
                <input data-testid="user-password-field" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputClass} required />
              </div>
              <button type="submit" disabled={busy} data-testid="submit-add-user" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-3.5 text-base font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
                <UserPlus size={18} /> {busy ? "Creating..." : "Create User"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function AdminHome() {
  const { user, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [grantTarget, setGrantTarget] = useState(null);
  const [bulkGrant, setBulkGrant] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await api.listUsers()); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.role})? This cannot be undone.`)) return;
    try { await api.deleteUser(u.id); toast.success(`${u.name} deleted`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleDisable = async (u) => {
    try {
      if (u.disabled) { await api.enableUser(u.id); toast.success(`${u.name} enabled`); }
      else { await api.disableUser(u.id); toast.success(`${u.name} disabled`); }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const revoke = async (u) => {
    try { await api.revokePlan(u.id); toast.success(`Plan removed from ${u.name}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doImpersonate = async (u) => {
    try { await impersonate(u.id); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const counts = users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {});
  const q = query.trim().toLowerCase();
  const roleFiltered = filter === "all" ? users : users.filter((u) => u.role === filter);
  const shown = !q ? roleFiltered : roleFiltered.filter((u) => {
    return [u.name, u.email, u.username].some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  const selectableShown = shown.filter((u) => u.id !== user?.id);
  const allShownSelected = selectableShown.length > 0 && selectableShown.every((u) => selected.has(u.id));
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) => {
    if (allShownSelected) return new Set();
    return new Set(selectableShown.map((u) => u.id));
  });
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected account(s)? This cannot be undone.`)) return;
    try {
      const res = await api.bulkDeleteUsers(ids);
      toast.success(`${res.deleted} account(s) deleted`);
      clearSelection();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const bulkGrantPlan = async (plan, seats) => {
    const ids = [...selected];
    const res = await api.bulkPlanUsers({ user_ids: ids, plan, max_students: seats });
    toast.success(`${res.plan ? "" : ""}Granted ${plan} to ${res.granted} account(s)${res.skipped ? ` (${res.skipped} skipped)` : ""}`);
    clearSelection();
    load();
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Platform Admin</div>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">All Users</h1>
          <p className="mt-1 font-body text-[#6B7280]">Create, remove, impersonate or bulk-manage any account.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-button" className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-5 py-3.5 font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5">
          <UserPlus size={18} /> Add User
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {["admin", "teacher", "parent", "student", "reseller", "school_admin", "district_admin"].map((r) => {
          const M = ROLE_META[r];
          return (
            <div key={r} className="rounded-2xl border border-[#E5DECF] bg-white p-4" data-testid={`stat-${r}`}>
              <div className="flex items-center gap-2"><M.Icon size={18} color={M.color} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">{r === "school_admin" ? "Schools" : r === "district_admin" ? "Districts" : `${M.label}s`}</span></div>
              <div className="mt-1 font-heading text-2xl font-extrabold text-[#1F2937]">{counts[r] || 0}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative w-full sm:max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="user-search"
            className="w-full rounded-full border border-[#E5DECF] bg-white pl-9 pr-9 py-2 font-body text-sm text-[#1F2937] outline-none focus:border-[#0D9488]"
            placeholder="Search name, email, username…"
            aria-label="Search users"
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="user-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#94A3B8] hover:bg-[#F2ECE1]" aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="user-filters">
          {["all", "teacher", "parent", "student", "reseller", "school_admin", "district_admin", "admin"].map((r) => (
            <button key={r} onClick={() => setFilter(r)} data-testid={`filter-${r}`}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${filter === r ? "bg-[#0D9488] text-white" : "bg-white border border-[#E5DECF] text-[#6B7280] hover:bg-[#F2ECE1]"}`}>
              {r === "all" ? "All" : r === "school_admin" ? "Schools" : r === "district_admin" ? "Districts" : ROLE_META[r].label}
            </button>
          ))}
        </div>
        {(query || filter !== "all") && (
          <div className="ml-auto font-body text-xs font-semibold text-[#6B7280]" data-testid="user-search-count">
            {shown.length} of {users.length} shown
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            data-testid="bulk-action-bar"
            className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#0D9488]/30 bg-[#0D9488]/5 px-4 py-3"
          >
            <span data-testid="bulk-selected-count" className="font-body text-sm font-bold text-[#0B7F74]">{selected.size} selected</span>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setBulkGrant(true)} data-testid="bulk-grant-button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#0D9488] px-3.5 py-2 text-sm font-bold text-white hover:bg-[#0B7F74]">
                <Crown size={15} /> Grant plan
              </button>
              <button onClick={bulkDelete} data-testid="bulk-delete-button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#E05A47] px-3.5 py-2 text-sm font-bold text-white hover:bg-[#B4402F]">
                <Trash2 size={15} /> Delete
              </button>
              <button onClick={clearSelection} data-testid="bulk-clear-button" className="rounded-xl px-3 py-2 text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1]">Clear</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6 overflow-x-auto rounded-3xl border border-[#E5DECF] bg-white">
        {loading ? (
          <div className="p-10 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading...</div>
        ) : (
          <table className="w-full text-left" data-testid="admin-users-table">
            <thead>
              <tr className="bg-[#F2ECE1] text-xs uppercase tracking-wide text-[#6B7280]">
                <th className="px-5 py-3">
                  <input type="checkbox" data-testid="select-all-checkbox" checked={allShownSelected} onChange={toggleSelectAll} className="h-4 w-4 cursor-pointer accent-[#0D9488]" aria-label="Select all" />
                </th>
                <th className="px-5 py-3 font-bold">Name</th>
                <th className="px-5 py-3 font-bold">Role</th>
                <th className="px-5 py-3 font-bold">Email / Username</th>
                <th className="px-5 py-3 font-bold">Plan / Status</th>
                <th className="px-5 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u) => {
                const M = ROLE_META[u.role] || ROLE_META.student;
                const isSelf = u.id === user?.id;
                const planned = u.subscription?.status === "active";
                const canPlan = ["teacher", "parent", "reseller", "school_admin", "district_admin"].includes(u.role);
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-t border-[#E5DECF] ${u.disabled ? "opacity-60" : ""} ${selected.has(u.id) ? "bg-[#0D9488]/5" : ""}`}>
                    <td className="px-5 py-4">
                      {!isSelf && (
                        <input type="checkbox" data-testid={`select-user-${u.id}`} checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} className="h-4 w-4 cursor-pointer accent-[#0D9488]" aria-label={`Select ${u.name}`} />
                      )}
                    </td>
                    <td className="px-5 py-4 font-body font-semibold text-[#1F2937]">
                      {u.name}
                      {u.brand && <span className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${u.brand.accent_color}1A`, color: u.brand.accent_color }}>{u.brand.name}</span>}
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${M.color}1A`, color: M.color }}>
                        <M.Icon size={13} /> {M.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-body text-[#6B7280]">{u.email || (u.username ? `@${u.username}` : "—")}</td>
                    <td className="px-5 py-4">
                      {planned ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-2.5 py-1 text-xs font-bold text-[#047857]" data-testid={`plan-badge-${u.id}`}>
                          <Crown size={12} /> {u.subscription.plan}{u.subscription.complimentary ? " (comp)" : ""}
                        </span>
                      ) : <span className="text-xs text-[#94A3B8]">Free</span>}
                      {u.disabled && <span className="ml-1 rounded-full bg-[#E05A47]/15 px-2 py-1 text-xs font-bold text-[#E05A47]">Disabled</span>}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {isSelf ? (
                        <span className="text-xs font-semibold text-[#94A3B8]">You</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => doImpersonate(u)} data-testid={`impersonate-${u.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#7A3FF2] hover:bg-[#7A3FF2]/10">
                            <LogIn size={14} /> Impersonate
                          </button>
                          {canPlan && (planned
                            ? <button onClick={() => revoke(u)} data-testid={`revoke-plan-${u.id}`} className="rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#B45309] hover:bg-[#F59E0B]/10">Revoke</button>
                            : <button onClick={() => setGrantTarget(u)} data-testid={`grant-plan-${u.id}`} className="rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#0D9488] hover:bg-[#0D9488]/10">Grant plan</button>)}
                          <button onClick={() => toggleDisable(u)} data-testid={`toggle-disable-${u.id}`} className={`inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold ${u.disabled ? "text-[#047857] hover:bg-[#10B981]/10" : "text-[#6B7280] hover:bg-[#F2ECE1]"}`}>
                            {u.disabled ? <><CircleCheck size={14} /> Enable</> : <><Ban size={14} /> Disable</>}
                          </button>
                          <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#E05A47] hover:bg-[#E05A47]/10">
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AddUserModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={load} />
      <GrantPlanModal
        open={!!grantTarget}
        title="Grant complimentary plan"
        subtitle={grantTarget ? `Give ${grantTarget.name} full access with no charge.` : ""}
        onClose={() => setGrantTarget(null)}
        onGrant={async (plan, seats) => {
          await api.grantPlan(grantTarget.id, { plan, max_students: seats });
          toast.success(`Granted ${plan} plan to ${grantTarget.name}`);
          load();
        }}
      />
      <GrantPlanModal
        open={bulkGrant}
        title="Grant plan to selected"
        subtitle={`Apply one complimentary plan to ${selected.size} selected account(s). Students are skipped.`}
        submitLabel="Grant to selected"
        onClose={() => setBulkGrant(false)}
        onGrant={bulkGrantPlan}
      />
    </div>
  );
}
